import Phaser from "phaser";
import { SolidMasks, framesByPage, pageImageKey, pageTextureKey, registerAtlasPage } from "daedalus-renderer-phaser";
import { pageUrl, type LoadedWorld } from "../editor/world-loader.ts";
import { say } from "../words.ts";
import { pagesInReach, pagesOfMap, pagesToDrop } from "./atlas-pages.ts";
import { pagesToRelease } from "./neighbours/reach.ts";
import type { CharacterStrip } from "./character.ts";

/**
 * The atlas pages the game page holds as textures (ADR-109 decisions 8 and 11, P1.4-T84): which of the pages a map names
 * are still to load, registering what arrived with its frames and solid pixels, and letting go of the pages no map in
 * reach uses, together with the masks read from them. A prop view keeps hidden images that name a texture until they are
 * reused with `setTexture`, so a dropped page is safe while the stage that drew it has ended.
 */

/**
 * The most atlas steps `steps` remembers.
 */
const STEPS_KEPT = 16;

/**
 * One atlas page as the loader and the texture manager know it: the key its image loads under, the key of its atlas
 * texture and its URL.
 */
export interface PageFile {
  readonly page: number;
  readonly key: string;
  readonly texture: string;
  readonly url: string;
}

/**
 * What a map still needs of the atlas: the pages it names, those not yet held (`missing`, loaded by the caller) and
 * those not yet registered as atlas textures (`fresh`).
 */
export interface AtlasPlan {
  readonly wanted: readonly number[];
  readonly missing: readonly PageFile[];
  readonly fresh: readonly number[];
}

/**
 * Loads page images; never rejects: null when every one landed, else why the first did not.
 */
export type PageLoader = (pages: readonly PageFile[]) => Promise<string | null>;

/**
 * What the holder is built over. `masks` makes the solid-pixel reader and `load` fetches page images; Phaser's own by
 * default.
 */
export interface AtlasHolderOptions {
  readonly masks?: (textures: Phaser.Textures.TextureManager, world: LoadedWorld) => SolidMasks;
  readonly load?: PageLoader;
}

/**
 * Why a file of a loader batch failed: its status when it had an answer.
 *
 * @param file The file Phaser's loader reported.
 * @returns `<url> responded <status>`, or `<url> did not answer`.
 */
function fileFailure(file: Phaser.Loader.File): string {
  const status = file.xhrLoader?.status ?? 0;
  const url = typeof file.src === "string" && file.src !== "" ? file.src : String(file.url);
  return status > 0 ? say("loading.file.responded", { url, status: String(status) }) : say("loading.file.unanswered", { url });
}

/**
 * Runs one batch of Phaser's loader over page images and character strips.
 *
 * @param scene The scene whose loader takes them.
 * @param pages The pages to load: each image's loader key and URL.
 * @param strips The strips to load.
 * @param onFile Called as each file lands in the texture manager.
 * @returns Null when every file landed, else why the first did not: its URL and status, or its URL when it arrived and
 *   could not be read.
 */
export function loadFiles(scene: Phaser.Scene, pages: readonly { readonly key: string; readonly url: string }[], strips: readonly CharacterStrip[], onFile: () => void): Promise<string | null> {
  const loader = scene.load;
  return new Promise((resolve) => {
    let failure: string | null = null;
    /**
     * Records the first failed file.
     *
     * @param file The file.
     */
    const onError = (file: Phaser.Loader.File): void => {
      failure ??= fileFailure(file);
    };
    loader.on(Phaser.Loader.Events.FILE_COMPLETE, onFile);
    loader.on(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
    loader.once(Phaser.Loader.Events.COMPLETE, () => {
      loader.off(Phaser.Loader.Events.FILE_COMPLETE, onFile);
      loader.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
      const unread = [...pages, ...strips].find((file) => !scene.textures.exists(file.key));
      resolve(failure ?? (unread === undefined ? null : say("loading.file.unread", { url: String(unread.url) })));
    });
    for (const page of pages) {
      loader.image(page.key, page.url);
    }
    for (const strip of strips) {
      loader.spritesheet(strip.key, strip.url, { frameWidth: strip.frameWidth, frameHeight: strip.frameHeight });
    }
    loader.start();
  });
}

/**
 * Holds the atlas pages of a scene.
 */
export class AtlasPageHolder {
  public masks: SolidMasks | null = null;
  private masksFailed = false;
  private stoodOn: string | null = null;
  private readonly pending = new Map<number, Promise<void>>();
  private heldNow: number[] = [];
  private readonly stepTotals: number[] = [];

  /**
   * @param scene The scene whose texture manager holds the pages.
   * @param options The solid-pixel reader and the page loader, where not Phaser's own.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly options: AtlasHolderOptions = {},
  ) {}

  /**
   * The map the page last stood on with its chunks loaded, whose pages stay held and which a failed change of map does
   * not forget.
   *
   * @returns The map's id, or null before the first.
   */
  public get standing(): string | null {
    return this.stoodOn;
  }

  /**
   * The indices of the atlas pages held as textures after the last change.
   *
   * @returns The indices, ascending.
   */
  public get held(): readonly number[] {
    return this.heldNow;
  }

  /**
   * The item count of each atlas step the loading screen was shown, oldest first, the last sixteen.
   *
   * @returns The counts.
   */
  public get steps(): readonly number[] {
    return this.stepTotals;
  }

  /**
   * Notes that the page stands on a map with its chunks loaded.
   *
   * @param mapId The map's id.
   */
  public stand(mapId: string): void {
    this.stoodOn = mapId;
  }

  /**
   * What a map still needs of the atlas, after any load of its pages that a preload has started finishes.
   *
   * @param world The loaded world.
   * @param mapId The map.
   * @returns The plan.
   */
  public async plan(world: LoadedWorld, mapId: string): Promise<AtlasPlan> {
    const wanted = pagesOfMap(world.manifest.maps, mapId, world.assets);
    await Promise.allSettled(wanted.map((page) => this.pending.get(page)));
    const textures = this.scene.textures;
    return {
      wanted,
      missing: wanted.map((page) => this.file(world, page)).filter((file) => !textures.exists(file.texture) && !textures.exists(file.key)),
      fresh: wanted.filter((page) => !textures.exists(pageTextureKey(world.assets, page))),
    };
  }

  /**
   * Notes that an atlas step of this many items was shown.
   *
   * @param total The step's item count.
   */
  public noteStep(total: number): void {
    this.stepTotals.push(total);
    if (this.stepTotals.length > STEPS_KEPT) {
      this.stepTotals.shift();
    }
  }

  /**
   * Registers the pages of a plan once their images have loaded, then lets go of the pages no map in reach of the map
   * uses: the map's, its neighbours' and the map just left, whose pages a way back through a door needs.
   *
   * @param world The loaded world.
   * @param plan The plan, its missing pages now loaded.
   * @param mapId The map the page stands on.
   * @throws Error when a page image was not loaded.
   */
  public finish(world: LoadedWorld, plan: AtlasPlan, mapId: string): void {
    this.register(world, plan.fresh);
    const left = this.stoodOn;
    const kept = left === null || left === mapId ? [] : [left];
    const held = this.heldPages(world);
    const dropped = pagesToDrop(held, pagesInReach(world.manifest.maps, mapId, world.assets, kept));
    for (const page of dropped) {
      this.scene.textures.remove(pageTextureKey(world.assets, page));
    }
    this.masks?.release(dropped);
    this.heldNow = held.filter((page) => !dropped.includes(page));
  }

  /**
   * Loads and registers the pages of a map the page is about to need, without the loading screen (P1.4-T191 calls it for a
   * neighbour whose seam the view is reaching). Pages held are not asked for again, and a page another call is already
   * loading is waited for, so calls for the same map made together share one load.
   *
   * @param world The loaded world.
   * @param mapId The map whose pages to load.
   * @throws Error naming the first file that failed, or one that arrived but could not be read.
   */
  public async preload(world: LoadedWorld, mapId: string): Promise<void> {
    const textures = this.scene.textures;
    const waits: Promise<void>[] = [];
    const start: number[] = [];
    for (const page of pagesOfMap(world.manifest.maps, mapId, world.assets)) {
      const running = this.pending.get(page);
      if (running !== undefined) {
        waits.push(running);
      } else if (!textures.exists(pageTextureKey(world.assets, page))) {
        start.push(page);
      }
    }
    if (start.length > 0) {
      const run = this.loadAndRegister(world, start);
      for (const page of start) {
        this.pending.set(page, run);
      }
      const clear = (): void => {
        for (const page of start) {
          this.pending.delete(page);
        }
      };
      run.then(clear, clear);
      waits.push(run);
    }
    await Promise.all(waits);
  }

  /**
   * Lets go of the pages of a neighbour no longer drawn beside the map the page stands on (ADR-147 decision 9), and their
   * masks: those the neighbour draws from that neither the map stood on nor any map kept draws from. A page still loading
   * for another call is kept.
   * @param world The loaded world.
   * @param mapId The neighbour let go.
   * @param kept The other neighbours still drawn.
   */
  public release(world: LoadedWorld, mapId: string, kept: readonly string[]): void {
    const standing = this.stoodOn === null ? [] : [this.stoodOn];
    const freed = pagesToRelease(world.manifest.maps, [mapId], [...standing, ...kept], world.assets).filter((page) => !this.pending.has(page));
    if (freed.length === 0) {
      return;
    }
    for (const page of freed) {
      this.scene.textures.remove(pageTextureKey(world.assets, page));
    }
    this.masks?.release(freed);
    this.heldNow = this.heldPages(world);
  }

  /**
   * Loads the images of pages not yet held and registers the pages.
   *
   * @param world The loaded world.
   * @param pages The page indices.
   * @throws Error naming what failed to load.
   */
  private async loadAndRegister(world: LoadedWorld, pages: readonly number[]): Promise<void> {
    const textures = this.scene.textures;
    const files = pages.map((page) => this.file(world, page)).filter((file) => !textures.exists(file.key));
    if (files.length > 0) {
      const failure = await (this.options.load ?? ((batch) => loadFiles(this.scene, batch, [], () => undefined)))(files);
      if (failure !== null) {
        throw new Error(failure);
      }
    }
    this.register(world, pages);
    this.heldNow = this.heldPages(world);
  }

  /**
   * The loader key, texture key and URL of one atlas page.
   *
   * @param world The loaded world.
   * @param page The page index.
   * @returns The file.
   */
  private file(world: LoadedWorld, page: number): PageFile {
    return { page, key: pageImageKey(world.assets, page), texture: pageTextureKey(world.assets, page), url: pageUrl(world, page) };
  }

  /**
   * Registers loaded page images as atlas textures with their frames, and readies their solid pixels. Pixels that cannot
   * be read only stop props fading over the character this session; the masks already readied for earlier pages are kept.
   *
   * @param world The loaded world.
   * @param pages The page indices whose images arrived and which are not textures yet.
   * @throws Error when a page image was not loaded.
   */
  private register(world: LoadedWorld, pages: readonly number[]): void {
    const textures = this.scene.textures;
    const fresh = pages.filter((page) => !textures.exists(pageTextureKey(world.assets, page)));
    if (fresh.length === 0) {
      return;
    }
    const frames = framesByPage(world.assets);
    for (const page of fresh) {
      registerAtlasPage(this.scene, world.assets, page, frames[page] ?? []);
    }
    if (this.masksFailed) {
      return;
    }
    try {
      const masks = this.masks ?? (this.options.masks ?? ((manager, loaded) => new SolidMasks(manager, loaded.assets)))(textures, world);
      masks.prepare(fresh);
      this.masks = masks;
    } catch (error) {
      this.masksFailed = true;
      console.warn(`props will not fade over the character this session: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * The indices of the atlas pages the texture manager holds as atlas textures.
   *
   * @param world The loaded world.
   * @returns The indices, ascending.
   */
  private heldPages(world: LoadedWorld): number[] {
    return world.assets.pages.map((_, page) => page).filter((page) => this.scene.textures.exists(pageTextureKey(world.assets, page)));
  }
}
